import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import UserProfile from './components/UserProfile';
import CDLInfo from './components/CDLInfo';
import CDLAdmin from './components/CDLAdmin';
import { PublicDashboard, PublicTournamentView, PublicMatchView } from './components/PublicView';
import { TournamentStats, TeamStatsCard, Leaderboard } from './components/Stats';
import LoadingSpinner from './components/LoadingSpinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { API } from './lib/api';
import '@/App.css';

// Configure axios interceptors
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

axios.interceptors.response.use((response) => response, (error) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
  }
  return Promise.reject(error);
});

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    m.addEventListener('change', handler);
    setMatches(m.matches);
    return () => m.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

function Dashboard() {
  const [tournaments, setTournaments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCDLInfo, setShowCDLInfo] = useState(false);
  const [showCDLAdmin, setShowCDLAdmin] = useState(false);
  const [newTournamentName, setNewTournamentName] = useState('');
  const [tournamentType, setTournamentType] = useState('single');
  const [tournamentFormat, setTournamentFormat] = useState('bo5');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const loadTournaments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/tournaments`);

      setTournaments(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const createTournament = async () => {
    if (!newTournamentName.trim()) return;
    try {
      const res = await axios.post(`${API}/tournaments`, { 
        name: newTournamentName,
        tournament_type: tournamentType,
        format: tournamentFormat
      });
      toast.success('Tournoi créé');
      setNewTournamentName('');
      setTournamentType('single');
      setTournamentFormat('bo5');
      setShowCreate(false);
      navigate(`/tournament/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur création');
    }
  };

  const deleteTournament = async (id, event) => {
    event.stopPropagation();
    if (!window.confirm('Supprimer ce tournoi ?')) return;
    try {
      await axios.delete(`${API}/tournaments/${id}`);
      toast.success('Tournoi supprimé');
      loadTournaments();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  const filteredTournaments = filter === 'all' 
    ? tournaments 
    : tournaments.filter(t => t.status === filter);

  const statusColors = {
    setup: 'text-[#4169e1]',
    in_progress: 'text-[#00ff9d]',
    completed: 'text-[#ffd700]'
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="tactical-grid fixed inset-0 opacity-20" />
      <div className="scan-line" />
      
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="slide-in flex-1 min-w-0">
                <h1 className="font-primary font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase text-[#ff4500]">
                BLACK OPS 7 <span className="text-white">TOURNAMENT</span>
              </h1>
                <p className="font-secondary text-xs text-muted-foreground mt-1 sm:mt-2 tracking-wider hidden sm:block">CDL COMMAND CENTER</p>
            </div>
              
              {/* Menu Desktop */}
              <div className="hidden sm:flex gap-2 sm:gap-4 items-center flex-wrap">
  {[
    { label: "ADMIN CDL", action: () => setShowCDLAdmin(true), testid: "cdl-admin-btn" },
    { label: "GUIDE CDL", action: () => setShowCDLInfo(true), testid: "cdl-info-btn" },
    { label: "CLASSEMENT", action: () => setShowLeaderboard(true), testid: "leaderboard-btn" },
    { label: "PROFIL", action: () => setShowProfile(true), testid: "profile-btn" },
    { label: "DÉCONNEXION", action: handleLogout, testid: "logout-btn" }
  ].map(btn => (
    <button
      key={btn.label}
      onClick={btn.action}
      data-testid={btn.testid}
      className="
        bg-zinc-800
        border border-orange-800
        text-orange-700
        font-bold
        uppercase
        tracking-widest
        px-3 sm:px-4 lg:px-6
        py-1.5 sm:py-2
        skew-x-[-12deg]
        hover:bg-orange-500
        hover:text-black
        transition-all
        font-secondary
        text-[10px] sm:text-xs
      "
    >
      <span className="block skew-x-[12deg]">
        {btn.label}
      </span>
    </button>
  ))}
</div>

              {/* Bouton Menu Mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden bg-zinc-800 border border-orange-800 text-orange-700 p-2 hover:bg-orange-500 hover:text-black transition-all"
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Menu Mobile Dropdown */}
            {mobileMenuOpen && (
              <div className="sm:hidden mt-4 pt-4 border-t border-white/10 animate-in slide-in-from-top">
                <div className="flex flex-col gap-2">
                  {[
                    { label: "ADMIN CDL", action: () => { setShowCDLAdmin(true); setMobileMenuOpen(false); }, testid: "mobile-cdl-admin-btn" },
                    { label: "GUIDE CDL", action: () => { setShowCDLInfo(true); setMobileMenuOpen(false); }, testid: "mobile-cdl-info-btn" },
                    { label: "CLASSEMENT", action: () => { setShowLeaderboard(true); setMobileMenuOpen(false); }, testid: "mobile-leaderboard-btn" },
                    { label: "PROFIL", action: () => { setShowProfile(true); setMobileMenuOpen(false); }, testid: "mobile-profile-btn" },
                    { label: "DÉCONNEXION", action: () => { handleLogout(); setMobileMenuOpen(false); }, testid: "mobile-logout-btn" }
                  ].map(btn => (
                    <button
                      key={btn.label}
                      onClick={btn.action}
                      data-testid={btn.testid}
                      className="
                        w-full
                        bg-zinc-800
                        border border-orange-800
                        text-orange-700
                        font-bold
                        uppercase
                        tracking-widest
                        px-4
                        py-3
                        skew-x-[-12deg]
                        hover:bg-orange-500
                        hover:text-black
                        transition-all
                        font-secondary
                        text-xs
                        text-left
                      "
                    >
                      <span className="block skew-x-[12deg]">
                        {btn.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h2 className="font-primary font-black text-xl sm:text-2xl uppercase tracking-tighter">TOURNOIS CDL</h2>
              <p className="font-secondary text-xs text-muted-foreground mt-1 hidden sm:block">GESTION DES COMPÉTITIONS BLACK OPS 7</p>
            </div>
            <button
              data-testid="create-tournament-btn"
              onClick={() => setShowCreate(true)}
              className="bg-[#ff4500] text-black font-bold uppercase tracking-widest px-4 sm:px-6 lg:px-8 py-2 sm:py-3 hover:bg-[#ff4500]/90 transition-all hover-lift clip-corner font-secondary text-xs sm:text-sm pulse-glow w-full sm:w-auto"
            >
              + NOUVEAU TOURNOI
            </button>
          </div>

          <div className="flex gap-2 sm:gap-4 mb-6 overflow-x-auto pb-2 -mx-4 sm:mx-0 px-4 sm:px-0">
            {['all', 'setup', 'in_progress', 'completed'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`font-secondary text-xs uppercase px-3 sm:px-4 py-2 border transition-colors whitespace-nowrap flex-shrink-0 ${
                  filter === f 
                    ? 'bg-[#ff4500] text-black border-[#ff4500]' 
                    : 'border-[#262626] text-muted-foreground hover:border-[#ff4500]/50'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'TOUS' : f === 'setup' ? 'CONFIG' : f === 'in_progress' ? 'EN COURS' : 'TERMINÉS'}
              </button>
            ))}
          </div>

          {showCreate && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in overflow-y-auto">
              <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 lg:p-8 max-w-md w-full corner-decoration relative slide-in my-4">
                <h3 className="font-primary font-black text-lg sm:text-xl uppercase mb-4 sm:mb-6">CRÉER UN TOURNOI</h3>
                <input
                  data-testid="tournament-name-input"
                  type="text"
                  value={newTournamentName}
                  onChange={(e) => setNewTournamentName(e.target.value)}
                  placeholder="Nom du tournoi"
                  className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary placeholder:text-muted-foreground/50 focus:outline-none transition-colors mb-4"
                  onKeyPress={(e) => e.key === 'Enter' && createTournament()}
                  autoFocus
                />
                
                <div className="mb-4">
                  <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">TYPE DE TOURNOI</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTournamentType('single')}
                      className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                        tournamentType === 'single'
                          ? 'bg-[#ff4500] text-black font-bold'
                          : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                      }`}
                    >
                      SIMPLE
                    </button>
                    <button
                      type="button"
                      onClick={() => setTournamentType('double')}
                      className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                        tournamentType === 'double'
                          ? 'bg-[#ff4500] text-black font-bold'
                          : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                      }`}
                    >
                      DOUBLE
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">FORMAT</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTournamentFormat('bo3')}
                      className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                        tournamentFormat === 'bo3'
                          ? 'bg-[#ff4500] text-black font-bold'
                          : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                      }`}
                    >
                      BO3
                    </button>
                    <button
                      type="button"
                      onClick={() => setTournamentFormat('bo5')}
                      className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                        tournamentFormat === 'bo5'
                          ? 'bg-[#ff4500] text-black font-bold'
                          : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                      }`}
                    >
                      BO5
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    data-testid="confirm-create-btn"
                    onClick={createTournament}
                    className="flex-1 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-xs sm:text-sm"
                  >
                    CRÉER
                  </button>
                  <button
                    data-testid="cancel-create-btn"
                    onClick={() => {
                      setShowCreate(false);
                      setNewTournamentName('');
                      setTournamentType('single');
                      setTournamentFormat('bo5');
                    }}
                    className="flex-1 border border-white/10 text-white font-bold uppercase tracking-widest px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-white/5 transition-colors font-secondary text-xs sm:text-sm"
                  >
                    ANNULER
                  </button>
                </div>
              </div>
            </div>
          )}

          {showLeaderboard && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in">
              <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-2xl w-full corner-decoration relative slide-in max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-primary font-black text-xl uppercase">CLASSEMENT GÉNÉRAL</h3>
                  <button
                    onClick={() => setShowLeaderboard(false)}
                    className="text-muted-foreground hover:text-white font-secondary text-xs uppercase"
                  >
                    FERMER
                  </button>
                </div>
                <Leaderboard />
              </div>
            </div>
          )}

          {showProfile && <UserProfile onClose={() => setShowProfile(false)} />}
          
          {showCDLInfo && <CDLInfo onClose={() => setShowCDLInfo(false)} />}
          
          {showCDLAdmin && <CDLAdmin onClose={() => setShowCDLAdmin(false)} />}

          {loading ? (
            <LoadingSpinner text="Chargement des tournois..." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredTournaments.map((t) => (
                <div
                  key={t.id}
                  className="bg-[#0a0a0a] border border-[#262626] p-6 hover:border-[#ff4500]/50 transition-all cursor-pointer corner-decoration relative group hover-lift team-card-hover slide-in"
                  onClick={() => navigate(`/tournament/${t.id}`)}
                  data-testid={`tournament-card-${t.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-primary font-bold text-lg uppercase">{t.name}</h3>
                      <p className={`font-secondary text-xs uppercase mt-1 ${statusColors[t.status]}`}>
                        {t.status === 'setup' ? '● Configuration' : t.status === 'in_progress' ? '● En cours' : '● Terminé'}
                      </p>
                    </div>
                    <button
                      data-testid={`delete-tournament-${t.id}`}
                      onClick={(e) => deleteTournament(t.id, e)}
                      className="text-red-500 hover:text-red-400 font-secondary text-xs uppercase transition-colors"
                    >
                      SUPPR
                    </button>
                  </div>
                  <div className="font-secondary text-xs text-muted-foreground">
                    <div className="flex justify-between py-2 border-t border-white/5">
                      <span>CRÉÉ LE</span>
                      <span>{new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {t.winner_id && (
                      <div className="mt-2 px-3 py-2 bg-[#ffd700]/10 border border-[#ffd700]/30">
                        <span className="text-[#ffd700] uppercase">🏆 Champion</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredTournaments.length === 0 && (
            <div className="text-center py-20">
              <p className="font-secondary text-muted-foreground">
                {filter === 'all' ? 'Aucun tournoi. Créez-en un nouveau.' : `Aucun tournoi ${filter === 'setup' ? 'en configuration' : filter === 'in_progress' ? 'en cours' : 'terminé'}.`}
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function TournamentDetails() {
  const { id } = useParams();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamLogoUrl, setTeamLogoUrl] = useState('');
  const [teamPlayers, setTeamPlayers] = useState('');
  const [logoInputMethod, setLogoInputMethod] = useState('url'); // 'url' or 'file'
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      const [tRes, teamsRes, matchesRes] = await Promise.all([
        axios.get(`${API}/tournaments/${id}`),
        axios.get(`${API}/tournaments/${id}/teams`),
        axios.get(`${API}/tournaments/${id}/matches`)
      ]);
      setTournament(tRes.data);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
    } catch (e) {
      toast.error('Erreur chargement');
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('L\'image est trop grande (max 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setTeamLogoUrl(e.target.result); // base64 data URL
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const addTeam = async () => {
    if (!teamName.trim()) return;
    const players = teamPlayers
      .split(/[,\n]/)
      .map(p => p.trim())
      .filter(Boolean);
    try {
      await axios.post(`${API}/tournaments/${id}/teams`, { 
        name: teamName,
        logo_url: teamLogoUrl.trim() || null,
        players
      });
      toast.success('Équipe ajoutée');
      setTeamName('');
      setTeamLogoUrl('');
      setTeamPlayers('');
      setLogoInputMethod('url');
      setShowAddTeam(false);
      loadData();
    } catch (e) {
      toast.error('Erreur ajout équipe');
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Supprimer cette équipe ?')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Équipe supprimée');
      loadData();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const generateBracket = async () => {
    try {
      await axios.post(`${API}/tournaments/${id}/generate-bracket`);
      toast.success('Bracket généré');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération bracket');
    }
  };

  if (!tournament) return <div className="min-h-screen bg-[#050505] flex items-center justify-center font-secondary text-muted-foreground">Chargement...</div>;

  // Group matches by bracket type and round
  const winnerBracket = {};
  const loserBracket = {};
  const grandFinals = [];
  
  matches.forEach(m => {
    if (m.bracket_type === 'grand_finals') {
      grandFinals.push(m);
    } else if (m.bracket_type === 'loser') {
      if (!loserBracket[m.bracket_round]) loserBracket[m.bracket_round] = [];
      loserBracket[m.bracket_round].push(m);
    } else {
      if (!winnerBracket[m.bracket_round]) winnerBracket[m.bracket_round] = [];
      winnerBracket[m.bracket_round].push(m);
    }
  });
  
  const isDoubleElimination = tournament.tournament_type === 'double';

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="tactical-grid fixed inset-0 opacity-20" />
      
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/')}
              className="font-secondary text-xs text-[#ff4500] uppercase mb-3 sm:mb-4 hover:text-[#ff4500]/80"
            >
              ← RETOUR
            </button>
            <h1 className="font-primary font-black text-xl sm:text-2xl lg:text-3xl tracking-tighter uppercase break-words">{tournament.name}</h1>
            <p className="font-secondary text-xs text-muted-foreground mt-1 sm:mt-2 tracking-wider uppercase">{tournament.status}</p>
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            <div className="lg:col-span-1">
              <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 corner-decoration relative">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="font-primary font-black text-lg sm:text-xl uppercase">ÉQUIPES</h2>
                  <button
                    data-testid="add-team-btn"
                    onClick={() => setShowAddTeam(true)}
                    className="bg-[#00ff9d] text-black font-bold uppercase tracking-widest px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-[#00ff9d]/90 transition-colors font-secondary text-[10px] sm:text-xs"
                  >
                    + AJOUTER
                  </button>
                </div>

                {teams.map((team) => (
                  <div key={team.id} className="flex items-center justify-between py-3 border-b border-white/5" data-testid={`team-item-${team.id}`}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="flex items-center gap-2 cursor-pointer hover:text-[#ff4500] transition-colors min-w-0 flex-1">
                          {team.logo_url && (
                            <img 
                              src={team.logo_url} 
                              alt={team.name} 
                              className="w-6 h-6 object-contain flex-shrink-0"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span className="font-secondary text-sm truncate">{team.name}</span>
                          {(team.players?.length > 0) && (
                            <span className="text-[10px] text-muted-foreground">({team.players.length})</span>
                          )}
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start">
                        <p className="font-secondary text-xs font-bold text-[#ff4500] uppercase mb-2">{team.name}</p>
                        {team.players?.length > 0 ? (
                          <ul className="space-y-1">
                            {team.players.map((p, i) => (
                              <li key={i} className="font-secondary text-xs text-white/90">{p}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>
                        )}
                      </PopoverContent>
                    </Popover>
                    <button
                      data-testid={`delete-team-${team.id}`}
                      onClick={() => deleteTeam(team.id)}
                      className="text-red-500 hover:text-red-400 font-secondary text-xs uppercase"
                    >
                      SUPPR
                    </button>
                  </div>
                ))}

                {teams.length === 0 && (
                  <p className="font-secondary text-xs text-muted-foreground text-center py-8">Aucune équipe</p>
                )}

                {tournament.status === 'setup' && teams.length >= 2 && (
                  <button
                    data-testid="generate-bracket-btn"
                    onClick={generateBracket}
                    className="w-full mt-6 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                  >
                    GÉNÉRER BRACKET
                  </button>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#0a0a0a] border border-[#262626] p-3 sm:p-4 corner-decoration relative">
                <h2 className="font-primary font-black text-base sm:text-lg uppercase mb-3">BRACKET</h2>

                {matches.length === 0 ? (
                  <p className="font-secondary text-xs text-muted-foreground text-center py-10 sm:py-20">Bracket non généré</p>
                ) : (
                  <div className="space-y-4">
                    {/* Winner Bracket */}
                    <div className="relative">
                      <h3 className="font-secondary text-xs text-[#ff4500] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <span className="w-1 h-3 bg-[#ff4500]"></span>
                        WINNER BRACKET
                          </h3>
                      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                        <div className="relative flex gap-4 sm:gap-6 lg:gap-8 min-w-max pb-4">
                          {Object.keys(winnerBracket).sort((a, b) => a - b).map((roundNum, roundIdx) => {
                            const roundNumInt = Number(roundNum);
                            const totalRounds = Object.keys(winnerBracket).length;
                            const isLastRound = roundIdx === Object.keys(winnerBracket).length - 1;
                            
                              // Calculate offset for pyramid shape - alignement vertical des rounds
                              // Le gap entre Q2-Q3 doit s'aligner avec le gap entre SF1-SF2
                              // Responsive: 143 parfait sur desktop, ~105 sur mobile (cartes plus petites)
                              const ROUND_LABEL_HEIGHT = 28;
                              const MATCH_CARD_HEIGHT = isDesktop ? 143 : 105;
                              const MATCH_GAP = 8; // gap-2 entre les cartes
                              const matchesInRound = winnerBracket[roundNum].length;
                              const matchesInFirstRound = winnerBracket[Object.keys(winnerBracket).sort((a, b) => a - b)[0]].length;
                              const totalHeightFirstRound = matchesInFirstRound * MATCH_CARD_HEIGHT + (matchesInFirstRound - 1) * MATCH_GAP;
                              const totalHeightCurrentRound = matchesInRound * MATCH_CARD_HEIGHT + (matchesInRound - 1) * MATCH_GAP;
                              const offsetTop = (totalHeightFirstRound - totalHeightCurrentRound) / 2;
                              
                              return (
                              <div key={roundNum} className="relative flex flex-col min-w-[180px] sm:min-w-[200px] lg:min-w-[220px]">
                                {/* Round Label - Fixed at top */}
                                <div className="text-center mb-1 sticky top-0 bg-[#0a0a0a] z-20 pb-1">
                                  <h4 className="font-secondary text-[10px] sm:text-xs text-[#ff4500] uppercase tracking-wider font-bold">
                                    {roundNumInt === totalRounds ? 'FINALE' : 
                                     roundNumInt === totalRounds - 1 ? 'DEMI-FINALE' :
                                     roundNumInt === totalRounds - 2 ? 'QUART DE FINALE' :
                                     `R${roundNum}`}
                                  </h4>
                                  <div className="h-px bg-gradient-to-r from-transparent via-[#ff4500]/30 to-transparent mt-1"></div>
                                </div>
                                
                                {/* Spacer for pyramid offset (haut) */}
                                <div style={{ height: `${offsetTop}px` }}></div>
                                
                                {/* Connection Lines SVG (between rounds) - top-0 pour aligner coordonnées y avec le contenu */}
                                {!isLastRound && (() => {
                                  const svgWidth = 24;
                                  const colHeight = ROUND_LABEL_HEIGHT + totalHeightFirstRound;
                                  const nextRoundMatches = winnerBracket[String(roundNumInt + 1)] || [];
                                  const nextRoundTotalHeight = nextRoundMatches.length * MATCH_CARD_HEIGHT + (nextRoundMatches.length - 1) * MATCH_GAP;
                                  const nextRoundOffset = (totalHeightFirstRound - nextRoundTotalHeight) / 2;
                                  const slotHeight = MATCH_CARD_HEIGHT + MATCH_GAP;
                                  return (
                                  <svg className="absolute top-0 -right-4 sm:-right-6 lg:-right-8 w-4 sm:w-6 lg:w-8 h-full pointer-events-none z-0" viewBox={`0 0 ${svgWidth} ${colHeight}`} preserveAspectRatio="none">
                                    {winnerBracket[roundNum].map((match, matchIdx) => {
                                      const nextMatchIdx = Math.floor(matchIdx / 2);
                                      const y1 = ROUND_LABEL_HEIGHT + offsetTop + matchIdx * slotHeight + MATCH_CARD_HEIGHT / 2;
                                      const y2 = ROUND_LABEL_HEIGHT + nextRoundOffset + nextMatchIdx * slotHeight + MATCH_CARD_HEIGHT / 2;
                                      
                                      return (
                                        <g key={match.id}>
                                          <line x1="0" y1={y1} x2={svgWidth} y2={y1} stroke="rgba(255, 69, 0, 0.4)" strokeWidth="2" strokeDasharray="4,4" />
                                          <line x1={svgWidth} y1={y1} x2={svgWidth} y2={y2} stroke="rgba(255, 69, 0, 0.4)" strokeWidth="2" strokeDasharray="4,4" />
                                          <polygon points={`${svgWidth},${y2} ${svgWidth-5},${y2-4} ${svgWidth-5},${y2+4}`} fill="rgba(255, 69, 0, 0.6)" />
                                        </g>
                                      );
                                    })}
                                  </svg>
                                  );
                                })()}
                                
                                {/* Matches - gap-2 (8px) pour aligner le gap Q2-Q3 avec le gap SF1-SF2 */}
                                <div className="relative z-10 flex flex-col gap-2">
                                  {winnerBracket[roundNum].map((match, matchIdx) => {
                            const team1 = teams.find(t => t.id === match.team1_id);
                            const team2 = teams.find(t => t.id === match.team2_id);
                            
                            return (
                              <div
                                key={match.id}
                                data-testid={`match-card-${match.id}`}
                                onClick={() => (match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed') && navigate(`/match/${match.id}`)}
                                        className={`relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border ${
                                          match.winner_id ? 'border-[#ff4500]/60' : 'border-[#262626]'
                                        } p-2 sm:p-2.5 shadow-md ${
                                  match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed'
                                            ? 'cursor-pointer hover:border-[#ff4500] hover:shadow-[#ff4500]/20 transition-all duration-200'
                                            : ''
                                        } transition-all duration-200`}
                                        style={{
                                          boxShadow: match.winner_id ? '0 0 10px rgba(255, 69, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.3)'
                                        }}
                                      >
                                        {/* Match number badge */}
                                        <div className="absolute -top-1 -right-1 bg-[#ff4500] text-black px-1.5 py-0.5 text-[8px] font-bold uppercase">
                                          M{match.match_number}
                                </div>
                                        
                                        {/* Status indicator */}
                                        <div className="absolute top-1.5 left-1.5">
                                          <div className={`w-1.5 h-1.5 rounded-full ${
                                            match.status === 'completed' ? 'bg-[#00ff9d]' :
                                            match.status === 'in_progress' ? 'bg-[#ff4500] animate-pulse' :
                                            match.status === 'ready' ? 'bg-[#ffd700]' :
                                            'bg-[#666]'
                                          }`}></div>
                                        </div>
                                        
                                        <div className="space-y-1 sm:space-y-1.5 mt-3">
                                          {/* Team 1 */}
                                          <div className={`relative flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-2.5 rounded ${
                                            match.winner_id === match.team1_id 
                                              ? 'bg-gradient-to-r from-[#ff4500]/30 to-[#ff4500]/10 border-l-2 border-[#ff4500]' 
                                              : 'bg-[#0a0a0a] border-l-2 border-transparent'
                                          } transition-all duration-200`}>
                                            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                              {team1?.logo_url && (
                                                <div className="relative flex-shrink-0">
                                                  <img 
                                                    src={team1.logo_url} 
                                                    alt={team1.name} 
                                                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                  />
                                                  {match.winner_id === match.team1_id && (
                                                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#ff4500] rounded-full border border-black"></div>
                                                  )}
                                                </div>
                                              )}
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <span onClick={(e) => e.stopPropagation()} className={`font-secondary text-xs sm:text-sm truncate cursor-pointer hover:underline ${
                                                    match.winner_id === match.team1_id ? 'font-bold text-[#ff4500]' : 'text-white'
                                                  }`}>
                                                    {team1?.name || 'TBD'}
                                                  </span>
                                                </PopoverTrigger>
                                                <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                                  <p className="font-secondary text-xs font-bold text-[#ff4500] uppercase mb-2">{team1?.name}</p>
                                                  {team1?.players?.length > 0 ? (
                                                    <ul className="space-y-1">{team1.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                                  ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                                </PopoverContent>
                                              </Popover>
                                  </div>
                                            <span className={`font-secondary text-sm sm:text-base font-black ml-2 flex-shrink-0 ${
                                              match.winner_id === match.team1_id ? 'text-[#ff4500]' : 'text-white/70'
                                  }`}>
                                              {match.team1_wins || 0}
                                            </span>
                                  </div>
                                          
                                          {/* VS Divider */}
                                          <div className="flex items-center justify-center py-0.5">
                                            <div className="h-px bg-gradient-to-r from-transparent via-[#ff4500]/40 to-transparent w-full"></div>
                                            <span className="px-1 text-[8px] text-[#ff4500]/60 font-bold uppercase">VS</span>
                                            <div className="h-px bg-gradient-to-r from-transparent via-[#ff4500]/40 to-transparent w-full"></div>
                                          </div>
                                          
                                          {/* Team 2 */}
                                          <div className={`relative flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-2.5 rounded ${
                                            match.winner_id === match.team2_id 
                                              ? 'bg-gradient-to-r from-[#ff4500]/30 to-[#ff4500]/10 border-l-2 border-[#ff4500]' 
                                              : 'bg-[#0a0a0a] border-l-2 border-transparent'
                                          } transition-all duration-200`}>
                                            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                              {team2?.logo_url && (
                                                <div className="relative flex-shrink-0">
                                                  <img 
                                                    src={team2.logo_url} 
                                                    alt={team2.name} 
                                                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                  />
                                                  {match.winner_id === match.team2_id && (
                                                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#ff4500] rounded-full border border-black"></div>
                                                  )}
                                                </div>
                                              )}
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <span onClick={(e) => e.stopPropagation()} className={`font-secondary text-xs sm:text-sm truncate cursor-pointer hover:underline ${
                                                    match.winner_id === match.team2_id ? 'font-bold text-[#ff4500]' : 'text-white'
                                                  }`}>
                                                    {team2?.name || 'TBD'}
                                                  </span>
                                                </PopoverTrigger>
                                                <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                                  <p className="font-secondary text-xs font-bold text-[#ff4500] uppercase mb-2">{team2?.name}</p>
                                                  {team2?.players?.length > 0 ? (
                                                    <ul className="space-y-1">{team2.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                                  ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                                </PopoverContent>
                                              </Popover>
                                            </div>
                                            <span className={`font-secondary text-sm sm:text-base font-black ml-2 flex-shrink-0 ${
                                              match.winner_id === match.team2_id ? 'text-[#ff4500]' : 'text-white/70'
                                            }`}>
                                              {match.team2_wins || 0}
                                            </span>
                                          </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                                {/* Spacer for pyramid offset (bas) - centre les matches dans la colonne étirée */}
                                <div style={{ height: `${offsetTop}px` }}></div>
                    </div>
                            );
                          })}
                  </div>
                      </div>
                    </div>

                    {/* Loser Bracket (only for double elimination) */}
                    {isDoubleElimination && Object.keys(loserBracket).length > 0 && (
                      <div className="mt-4 relative">
                        <h3 className="font-secondary text-xs text-[#00ff9d] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <span className="w-1 h-3 bg-[#00ff9d]"></span>
                          LOSER BRACKET
                        </h3>
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                          <div className="relative flex gap-4 sm:gap-6 lg:gap-8 min-w-max pb-4">
                            {Object.keys(loserBracket).sort((a, b) => a - b).map((roundNum, roundIdx) => {
                              const roundNumInt = Number(roundNum);
                              const isLastRound = roundIdx === Object.keys(loserBracket).length - 1;
                              
                              // Calculate offset for pyramid shape (loser bracket)
                              const LOSER_ROUND_LABEL_HEIGHT = 28;
                              const LOSER_MATCH_CARD_HEIGHT = isDesktop ? 143 : 105;
                              const LOSER_MATCH_GAP = 8;
                              const matchesInRound = loserBracket[roundNum].length;
                              const firstLoserRoundKey = Object.keys(loserBracket).sort((a, b) => a - b)[0];
                              const matchesInFirstRound = loserBracket[firstLoserRoundKey].length;
                              const totalHeightFirstRound = matchesInFirstRound * LOSER_MATCH_CARD_HEIGHT + (matchesInFirstRound - 1) * LOSER_MATCH_GAP;
                              const totalHeightCurrentRound = matchesInRound * LOSER_MATCH_CARD_HEIGHT + (matchesInRound - 1) * LOSER_MATCH_GAP;
                              const offsetTop = (totalHeightFirstRound - totalHeightCurrentRound) / 2;
                              
                              return (
                                <div key={roundNum} className="relative flex flex-col min-w-[180px] sm:min-w-[200px] lg:min-w-[220px]">
                                  {/* Round Label - Fixed at top */}
                                  <div className="text-center mb-1 sticky top-0 bg-[#0a0a0a] z-20 pb-1">
                                    <h4 className="font-secondary text-[10px] sm:text-xs text-[#00ff9d] uppercase tracking-wider font-bold">
                                      L{roundNum}
                                    </h4>
                                    <div className="h-px bg-gradient-to-r from-transparent via-[#00ff9d]/30 to-transparent mt-1"></div>
                                  </div>
                                  
                                  {/* Spacer for pyramid offset */}
                                  <div style={{ height: `${offsetTop}px` }}></div>
                                  
                                  {/* Connection Lines SVG */}
                                  {!isLastRound && (() => {
                                    const svgWidth = 24;
                                    const colHeight = LOSER_ROUND_LABEL_HEIGHT + totalHeightFirstRound;
                                    const nextRoundMatches = loserBracket[String(roundNumInt + 1)] || [];
                                    const nextRoundTotalHeight = nextRoundMatches.length * LOSER_MATCH_CARD_HEIGHT + (nextRoundMatches.length - 1) * LOSER_MATCH_GAP;
                                    const nextRoundOffset = (totalHeightFirstRound - nextRoundTotalHeight) / 2;
                                    const slotHeight = LOSER_MATCH_CARD_HEIGHT + LOSER_MATCH_GAP;
                                    return (
                                    <svg className="absolute top-0 -right-4 sm:-right-6 lg:-right-8 w-4 sm:w-6 lg:w-8 h-full pointer-events-none z-0" viewBox={`0 0 ${svgWidth} ${colHeight}`} preserveAspectRatio="none">
                                      {loserBracket[roundNum].map((match, matchIdx) => {
                                        const nextMatchIdx = Math.floor(matchIdx / 2);
                                        const y1 = LOSER_ROUND_LABEL_HEIGHT + offsetTop + matchIdx * slotHeight + LOSER_MATCH_CARD_HEIGHT / 2;
                                        const y2 = LOSER_ROUND_LABEL_HEIGHT + nextRoundOffset + nextMatchIdx * slotHeight + LOSER_MATCH_CARD_HEIGHT / 2;
                                        return (
                                          <g key={match.id}>
                                            <line x1="0" y1={y1} x2={svgWidth} y2={y1} stroke="rgba(0, 255, 157, 0.4)" strokeWidth="2" strokeDasharray="4,4" />
                                            <line x1={svgWidth} y1={y1} x2={svgWidth} y2={y2} stroke="rgba(0, 255, 157, 0.4)" strokeWidth="2" strokeDasharray="4,4" />
                                            <polygon points={`${svgWidth},${y2} ${svgWidth-5},${y2-4} ${svgWidth-5},${y2+4}`} fill="rgba(0, 255, 157, 0.6)" />
                                          </g>
                                        );
                                      })}
                                    </svg>
                                    );
                                  })()}
                                  
                                  {/* Matches - gap-2 (8px) pour alignement cohérent */}
                                  <div className="relative z-10 flex flex-col gap-2">
                                    {loserBracket[roundNum].map((match) => {
                                      const team1 = teams.find(t => t.id === match.team1_id);
                                      const team2 = teams.find(t => t.id === match.team2_id);
                                      
                                      return (
                                        <div
                                          key={match.id}
                                          data-testid={`match-card-${match.id}`}
                                          onClick={() => (match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed') && navigate(`/match/${match.id}`)}
                                          className={`relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border ${
                                            match.winner_id ? 'border-[#00ff9d]/60' : 'border-[#262626]'
                                          } p-2 sm:p-2.5 shadow-md ${
                                            match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed'
                                              ? 'cursor-pointer hover:border-[#00ff9d] hover:shadow-[#00ff9d]/20 transition-all duration-200'
                                              : ''
                                          } transition-all duration-200`}
                                          style={{
                                            boxShadow: match.winner_id ? '0 0 10px rgba(0, 255, 157, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.3)'
                                          }}
                                        >
                                          <div className="absolute -top-1 -right-1 bg-[#00ff9d] text-black px-1.5 py-0.5 text-[8px] font-bold uppercase">
                                            M{match.match_number}
              </div>
                                          <div className="absolute top-1.5 left-1.5">
                                            <div className={`w-1.5 h-1.5 rounded-full ${
                                              match.status === 'completed' ? 'bg-[#00ff9d]' :
                                              match.status === 'in_progress' ? 'bg-[#00ff9d] animate-pulse' :
                                              match.status === 'ready' ? 'bg-[#ffd700]' :
                                              'bg-[#666]'
                                            }`}></div>
                                          </div>
                                          
                                          <div className="space-y-1 sm:space-y-1.5 mt-3">
                                            <div className={`relative flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-2.5 rounded ${
                                              match.winner_id === match.team1_id 
                                                ? 'bg-gradient-to-r from-[#00ff9d]/30 to-[#00ff9d]/10 border-l-2 border-[#00ff9d]' 
                                                : 'bg-[#0a0a0a] border-l-2 border-transparent'
                                            } transition-all duration-200`}>
                                              <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                                {team1?.logo_url && (
                                                  <div className="relative flex-shrink-0">
                                                    <img src={team1.logo_url} alt={team1.name} className="w-5 h-5 sm:w-6 sm:h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                                    {match.winner_id === match.team1_id && (
                                                      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#00ff9d] rounded-full border border-black"></div>
                                                    )}
                                                  </div>
                                                )}
                                                <Popover>
                                                  <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <span className={`font-secondary text-xs sm:text-sm truncate cursor-pointer ${
                                                      match.winner_id === match.team1_id ? 'font-bold text-[#00ff9d]' : 'text-white'
                                                    }`}>
                                                      {team1?.name || 'TBD'}
                                                    </span>
                                                  </PopoverTrigger>
                                                  <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                                    <p className="font-secondary text-xs font-bold text-[#00ff9d] uppercase mb-2">{team1?.name}</p>
                                                    {team1?.players?.length > 0 ? (
                                                      <ul className="space-y-1">{team1.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                                    ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                                  </PopoverContent>
                                                </Popover>
                                              </div>
                                              <span className={`font-secondary text-sm sm:text-base font-black ml-2 flex-shrink-0 ${
                                                match.winner_id === match.team1_id ? 'text-[#00ff9d]' : 'text-white/70'
                                              }`}>
                                                {match.team1_wins || 0}
                                              </span>
                                            </div>
                                            
                                            <div className="flex items-center justify-center py-0.5">
                                              <div className="h-px bg-gradient-to-r from-transparent via-[#00ff9d]/40 to-transparent w-full"></div>
                                              <span className="px-1 text-[8px] text-[#00ff9d]/60 font-bold uppercase">VS</span>
                                              <div className="h-px bg-gradient-to-r from-transparent via-[#00ff9d]/40 to-transparent w-full"></div>
                                            </div>
                                            
                                            <div className={`relative flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-2.5 rounded ${
                                              match.winner_id === match.team2_id 
                                                ? 'bg-gradient-to-r from-[#00ff9d]/30 to-[#00ff9d]/10 border-l-2 border-[#00ff9d]' 
                                                : 'bg-[#0a0a0a] border-l-2 border-transparent'
                                            } transition-all duration-200`}>
                                              <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                                {team2?.logo_url && (
                                                  <div className="relative flex-shrink-0">
                                                    <img src={team2.logo_url} alt={team2.name} className="w-5 h-5 sm:w-6 sm:h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                                    {match.winner_id === match.team2_id && (
                                                      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#00ff9d] rounded-full border border-black"></div>
                                                    )}
                                                  </div>
                                                )}
                                                <Popover>
                                                  <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <span className={`font-secondary text-xs sm:text-sm truncate cursor-pointer ${
                                                      match.winner_id === match.team2_id ? 'font-bold text-[#00ff9d]' : 'text-white'
                                                    }`}>
                                                      {team2?.name || 'TBD'}
                                                    </span>
                                                  </PopoverTrigger>
                                                  <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                                    <p className="font-secondary text-xs font-bold text-[#00ff9d] uppercase mb-2">{team2?.name}</p>
                                                    {team2?.players?.length > 0 ? (
                                                      <ul className="space-y-1">{team2.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                                    ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                                  </PopoverContent>
                                                </Popover>
                                              </div>
                                              <span className={`font-secondary text-sm sm:text-base font-black ml-2 flex-shrink-0 ${
                                                match.winner_id === match.team2_id ? 'text-[#00ff9d]' : 'text-white/70'
                                              }`}>
                                                {match.team2_wins || 0}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Spacer for pyramid offset (bas) */}
                                  <div style={{ height: `${offsetTop}px` }}></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Grand Finals (only for double elimination) */}
                    {isDoubleElimination && grandFinals.length > 0 && (
                      <div className="mt-4 relative">
                        <div className="text-center mb-3">
                          <h3 className="font-secondary text-sm text-[#ffd700] uppercase tracking-wider mb-1 flex items-center justify-center gap-2">
                            <span className="w-4 h-0.5 bg-gradient-to-r from-transparent to-[#ffd700]/50"></span>
                            <span className="text-lg">🏆</span>
                            GRAND FINALS
                            <span className="text-lg">🏆</span>
                            <span className="w-4 h-0.5 bg-gradient-to-l from-transparent to-[#ffd700]/50"></span>
                          </h3>
                          <div className="h-px bg-gradient-to-r from-transparent via-[#ffd700]/50 to-transparent"></div>
                        </div>
                        <div className="flex justify-center">
                          {grandFinals.map((match) => {
                            const team1 = teams.find(t => t.id === match.team1_id);
                            const team2 = teams.find(t => t.id === match.team2_id);
                            
                            return (
                              <div
                                key={match.id}
                                data-testid={`match-card-${match.id}`}
                                onClick={() => (match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed') && navigate(`/match/${match.id}`)}
                                className={`relative bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#1a1a1a] border-2 ${
                                  match.winner_id ? 'border-[#ffd700]' : 'border-[#ffd700]/40'
                                } p-4 sm:p-5 shadow-xl max-w-sm w-full ${
                                  match.status === 'ready' || match.status === 'in_progress' || match.status === 'completed'
                                    ? 'cursor-pointer hover:border-[#ffd700] hover:shadow-[#ffd700]/30 transition-all duration-200'
                                    : ''
                                } transition-all duration-200`}
                                style={{
                                  boxShadow: match.winner_id ? '0 0 20px rgba(255, 215, 0, 0.2)' : '0 4px 8px rgba(0, 0, 0, 0.4)'
                                }}
                              >
                                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-[#ffd700] text-black px-3 py-0.5 text-[10px] font-bold uppercase">
                                  GRAND FINALS
                                </div>
                                
                                <div className="absolute top-2 right-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    match.status === 'completed' ? 'bg-[#ffd700]' :
                                    match.status === 'in_progress' ? 'bg-[#ffd700] animate-pulse' :
                                    match.status === 'ready' ? 'bg-[#ffd700]' :
                                    'bg-[#666]'
                                  }`}></div>
                                </div>
                                
                                <div className="space-y-2 sm:space-y-2.5 mt-5">
                                  <div className={`relative flex items-center justify-between py-2 sm:py-2.5 px-3 sm:px-4 rounded ${
                                    match.winner_id === match.team1_id 
                                      ? 'bg-gradient-to-r from-[#ffd700]/40 to-[#ffd700]/20 border-l-3 border-[#ffd700]' 
                                      : 'bg-[#0a0a0a] border-l-3 border-transparent'
                                  } transition-all duration-200`}>
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                      {team1?.logo_url && (
                                        <div className="relative flex-shrink-0">
                                          <img src={team1.logo_url} alt={team1.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                          {match.winner_id === match.team1_id && (
                                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#ffd700] rounded-full border border-black flex items-center justify-center">
                                              <span className="text-[6px]">👑</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <Popover>
                                        <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                          <span className={`font-secondary text-sm sm:text-base truncate cursor-pointer ${
                                            match.winner_id === match.team1_id ? 'font-bold text-[#ffd700]' : 'text-white'
                                          }`}>
                                            {team1?.name || 'TBD'}
                                          </span>
                                        </PopoverTrigger>
                                        <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                          <p className="font-secondary text-xs font-bold text-[#ffd700] uppercase mb-2">{team1?.name}</p>
                                          {team1?.players?.length > 0 ? (
                                            <ul className="space-y-1">{team1.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                          ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <span className={`font-secondary text-lg sm:text-xl font-black ml-3 flex-shrink-0 ${
                                      match.winner_id === match.team1_id ? 'text-[#ffd700]' : 'text-white/70'
                                    }`}>
                                      {match.team1_wins || 0}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-center py-1">
                                    <div className="h-px bg-gradient-to-r from-transparent via-[#ffd700]/60 to-transparent w-full"></div>
                                    <span className="px-2 text-xs text-[#ffd700] font-bold uppercase">VS</span>
                                    <div className="h-px bg-gradient-to-r from-transparent via-[#ffd700]/60 to-transparent w-full"></div>
                                  </div>
                                  
                                  <div className={`relative flex items-center justify-between py-2 sm:py-2.5 px-3 sm:px-4 rounded ${
                                    match.winner_id === match.team2_id 
                                      ? 'bg-gradient-to-r from-[#ffd700]/40 to-[#ffd700]/20 border-l-3 border-[#ffd700]' 
                                      : 'bg-[#0a0a0a] border-l-3 border-transparent'
                                  } transition-all duration-200`}>
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                      {team2?.logo_url && (
                                        <div className="relative flex-shrink-0">
                                          <img src={team2.logo_url} alt={team2.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                          {match.winner_id === match.team2_id && (
                                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#ffd700] rounded-full border border-black flex items-center justify-center">
                                              <span className="text-[6px]">👑</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <Popover>
                                        <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                          <span className={`font-secondary text-sm sm:text-base truncate cursor-pointer ${
                                            match.winner_id === match.team2_id ? 'font-bold text-[#ffd700]' : 'text-white'
                                          }`}>
                                            {team2?.name || 'TBD'}
                                          </span>
                                        </PopoverTrigger>
                                        <PopoverContent className="bg-[#0a0a0a] border-[#262626] w-auto max-w-[200px]" align="start" onClick={(e) => e.stopPropagation()}>
                                          <p className="font-secondary text-xs font-bold text-[#ffd700] uppercase mb-2">{team2?.name}</p>
                                          {team2?.players?.length > 0 ? (
                                            <ul className="space-y-1">{team2.players.map((p, i) => <li key={i} className="font-secondary text-xs text-white/90">{p}</li>)}</ul>
                                          ) : <p className="font-secondary text-xs text-muted-foreground">Aucun joueur</p>}
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <span className={`font-secondary text-lg sm:text-xl font-black ml-3 flex-shrink-0 ${
                                      match.winner_id === match.team2_id ? 'text-[#ffd700]' : 'text-white/70'
                                    }`}>
                                      {match.team2_wins || 0}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {showAddTeam && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 lg:p-8 max-w-md w-full corner-decoration relative my-4">
            <h3 className="font-primary font-black text-lg sm:text-xl uppercase mb-4 sm:mb-6">AJOUTER UNE ÉQUIPE</h3>
            <input
              data-testid="team-name-input"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Nom de l'équipe"
              className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-2.5 sm:py-3 text-sm font-secondary placeholder:text-muted-foreground/50 focus:outline-none transition-colors mb-4"
              onKeyPress={(e) => e.key === 'Enter' && addTeam()}
            />
            <textarea
              value={teamPlayers}
              onChange={(e) => setTeamPlayers(e.target.value)}
              placeholder="Joueurs (un par ligne ou séparés par des virgules)"
              rows={3}
              className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-2.5 text-sm font-secondary placeholder:text-muted-foreground/50 focus:outline-none transition-colors mb-4 sm:mb-6 resize-none"
            />
            
            {/* Logo Input Section */}
            <div className="mb-4 sm:mb-6">
              <div className="flex gap-2 mb-3 sm:mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setLogoInputMethod('url');
                    setTeamLogoUrl('');
                  }}
                  className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                    logoInputMethod === 'url'
                      ? 'bg-[#ff4500] text-black font-bold'
                      : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                  }`}
                >
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLogoInputMethod('file');
                    setTeamLogoUrl('');
                  }}
                  className={`flex-1 px-4 py-2 text-xs font-secondary uppercase transition-colors ${
                    logoInputMethod === 'file'
                      ? 'bg-[#ff4500] text-black font-bold'
                      : 'bg-[#1f1f22] text-white/70 hover:bg-[#2a2a2a]'
                  }`}
                >
                  FICHIER
                </button>
              </div>

              {logoInputMethod === 'url' ? (
                <input
                  type="text"
                  value={teamLogoUrl}
                  onChange={(e) => setTeamLogoUrl(e.target.value)}
                  placeholder="URL du logo (optionnel)"
                  className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary placeholder:text-muted-foreground/50 focus:outline-none transition-colors"
                  onKeyPress={(e) => e.key === 'Enter' && addTeam()}
                />
              ) : (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed transition-colors ${
                    isDragging
                      ? 'border-[#ff4500] bg-[#ff4500]/10'
                      : 'border-white/20 bg-[#1f1f22] hover:border-white/40'
                  } rounded p-6 text-center cursor-pointer`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileSelect(file);
                    }}
                    className="hidden"
                    id="logo-file-input"
                  />
                  <label htmlFor="logo-file-input" className="cursor-pointer">
                    <div className="space-y-2">
                      <p className="font-secondary text-sm text-white/70">
                        {isDragging ? 'Déposez l\'image ici' : 'Glissez-déposez une image ou cliquez pour sélectionner'}
                      </p>
                      <p className="font-secondary text-xs text-muted-foreground">
                        PNG, JPG, GIF (max 5MB)
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {teamLogoUrl && (
              <div className="mb-4 sm:mb-6 flex justify-center">
                <div className="relative">
                  <img 
                    src={teamLogoUrl} 
                    alt="Preview logo" 
                    className="max-w-[100px] sm:max-w-[120px] max-h-[100px] sm:max-h-[120px] object-contain border border-white/10 p-2 sm:p-3 bg-[#1f1f22]"
                    onError={(e) => { 
                      e.target.style.display = 'none';
                      toast.error('Impossible de charger l\'image');
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setTeamLogoUrl('')}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="confirm-add-team-btn"
                onClick={addTeam}
                className="flex-1 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-xs sm:text-sm"
              >
                AJOUTER
              </button>
              <button
                data-testid="cancel-add-team-btn"
                onClick={() => {
                  setShowAddTeam(false);
                  setTeamName('');
                  setTeamLogoUrl('');
                  setTeamPlayers('');
                  setLogoInputMethod('url');
                  setIsDragging(false);
                }}
                className="flex-1 border border-white/10 text-white font-bold uppercase tracking-widest px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-white/5 transition-colors font-secondary text-xs sm:text-sm"
              >
                ANNULER
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchView() {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState({});
  const [tournament, setTournament] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [team1Score, setTeam1Score] = useState(0);
  const [team2Score, setTeam2Score] = useState(0);
  const [scoreImageUrl, setScoreImageUrl] = useState('');
  const [playerStats, setPlayerStats] = useState({});
  const [editingRound, setEditingRound] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [maps, setMaps] = useState([]);
  const [selectedBans, setSelectedBans] = useState([]);
  const [banStep, setBanStep] = useState(0); // 0-5 pour demi/finale wizard
  const navigate = useNavigate();

  const BAN_STEPS_SEMI_FINAL = [
    { mode: 'Hardpoint', team: 'team1', label: 'Équipe 1' },
    { mode: 'Hardpoint', team: 'team2', label: 'Équipe 2' },
    { mode: 'Search & Destroy', team: 'team2', label: 'Équipe 2' },
    { mode: 'Search & Destroy', team: 'team1', label: 'Équipe 1' },
    { mode: 'Overload', team: 'team1', label: 'Équipe 1' },
    { mode: 'Overload', team: 'team2', label: 'Équipe 2' },
  ];

  const loadMatch = async () => {
    try {
      const res = await axios.get(`${API}/matches/${id}`);
      const matchData = res.data;
      setMatch(matchData);

      if (matchData.team1_id) {
        const [teamsRes, tournamentRes] = await Promise.all([
          axios.get(`${API}/tournaments/${matchData.tournament_id}/teams`),
          axios.get(`${API}/tournaments/${matchData.tournament_id}`)
        ]);
        const teamsMap = {};
        teamsRes.data.forEach(t => teamsMap[t.id] = t);
        setTeams(teamsMap);
        setTournament(tournamentRes.data);
      }

      if (matchData.rounds && matchData.rounds.length > 0) {
        const firstIncomplete = matchData.rounds.findIndex(r => !r.completed);
        setCurrentRound(firstIncomplete !== -1 ? firstIncomplete : 0);
      }
    } catch (e) {
      toast.error('Erreur chargement match');
    }
  };

  useEffect(() => {
    loadMatch();
  }, [id]);

  const openBanModal = async () => {
    try {
      const res = await axios.get(`${API}/maps`);
      setMaps(res.data.maps || []);
      setSelectedBans([]);
      setBanStep(0);
      setShowBanModal(true);
    } catch (e) {
      toast.error('Erreur chargement des maps');
    }
  };

  const selectBanSemiFinal = (mapName, mode) => {
    const newBan = { map_name: mapName, mode };
    setSelectedBans(prev => [...prev, newBan]);
    setBanStep(prev => Math.min(prev + 1, 6));
  };

  const getAvailableForMode = (mode) => {
    return maps
      .filter(m => (m.modes || []).includes(mode))
      .map(m => ({ map: m, mode }))
      .filter(({ map }) => !selectedBans.some(b => b.map_name === map.name && b.mode === mode));
  };

  const toggleBan = (mapName, mode) => {
    const key = `${mapName}_${mode}`;
    const exists = selectedBans.find(b => `${b.map_name}_${b.mode}` === key);
    if (exists) {
      setSelectedBans(prev => prev.filter(b => `${b.map_name}_${b.mode}` !== key));
    } else if (selectedBans.length < 2) {
      setSelectedBans(prev => [...prev, { map_name: mapName, mode }]);
    }
  };

  const isBanned = (mapName, mode) => selectedBans.some(b => b.map_name === mapName && b.mode === mode);

  const startMatch = async (bans = []) => {
    try {
      await axios.post(`${API}/matches/${id}/start`, { bans });
      const formatText = tournament?.format === 'bo3' ? '3 rounds' : '5 rounds';
      toast.success(`Match démarré - ${formatText} générés avec maps CDL`);
      setShowBanModal(false);
      loadMatch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur démarrage');
    }
  };

  const getPlayersList = () => {
    const t1 = teams[match?.team1_id];
    const t2 = teams[match?.team2_id];
    const list = [];
    (t1?.players || []).forEach(p => list.push({ name: p, teamId: match.team1_id, teamName: t1?.name }));
    (t2?.players || []).forEach(p => list.push({ name: p, teamId: match.team2_id, teamName: t2?.name }));
    return list;
  };

  const playerKey = (teamId, playerName) => `${teamId}_${playerName}`;

  const handleScoreImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image valide');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setScoreImageUrl(reader.result);
      }
    };
    reader.onerror = () => {
      toast.error("Impossible de lire l'image");
    };
    reader.readAsDataURL(file);
  };

  const buildPlayerStatsPayload = (stats, mode) => {
    const base = { kills: stats?.kills || 0, deaths: stats?.deaths || 0 };
    if (mode === 'Hardpoint' && stats?.time_on_point != null) base.time_on_point = stats.time_on_point;
    if (mode === 'Search & Destroy') {
      if (stats?.defuses != null) base.defuses = stats.defuses;
      if (stats?.plants != null) base.plants = stats.plants;
    }
    if (mode === 'Overload' && stats?.links != null) base.links = stats.links;
    return base;
  };

  const submitRound = async (roundNum = null) => {
    const targetRound = roundNum !== null ? roundNum : currentRound;
    const r = match.rounds[targetRound];
    const mode = r?.mode || '';
    const players = getPlayersList();
    
    try {
      const payload = {
        round_number: targetRound + 1,
        team1_score: team1Score,
        team2_score: team2Score,
        score_image_url: scoreImageUrl.trim()
      };
      payload.player_stats = players.map(({ name, teamId }) => ({
        player_name: name,
        team_id: teamId,
        ...buildPlayerStatsPayload(playerStats[playerKey(teamId, name)], mode)
      }));
      
      await axios.put(`${API}/matches/${id}/round`, payload);
      toast.success('Round enregistré');
      setTeam1Score(0);
      setTeam2Score(0);
      setScoreImageUrl('');
      setPlayerStats({});
      setEditingRound(null);
      loadMatch();
    } catch (e) {
      toast.error('Erreur enregistrement');
    }
  };

  const editRound = (roundIdx) => {
    const round = match.rounds[roundIdx];
    setTeam1Score(round.team1_score);
    setTeam2Score(round.team2_score);
    setScoreImageUrl(round.score_image_url || '');
    const next = {};
    (round.player_stats || []).forEach(s => {
      next[playerKey(s.team_id, s.player_name)] = {
        kills: s.kills ?? 0, deaths: s.deaths ?? 0,
        time_on_point: s.time_on_point ?? null, defuses: s.defuses ?? null,
        plants: s.plants ?? null, links: s.links ?? null
      };
    });
    setPlayerStats(next);
    setEditingRound(roundIdx);
    setCurrentRound(roundIdx);
  };

  if (!match) return <div className="min-h-screen bg-[#050505] flex items-center justify-center font-secondary text-muted-foreground">Chargement...</div>;

  const team1 = teams[match.team1_id];
  const team2 = teams[match.team2_id];
  const round = match.rounds?.[currentRound];
  const format = tournament?.format || 'bo5';
  const totalRounds = tournament?.total_rounds ?? 3;
  const isQuartersSingleElim = tournament?.tournament_type === 'single' && match.bracket_round === totalRounds - 2;
  const formatText = isQuartersSingleElim
    ? 'BEST OF 1 - PREMIER À 1 ROUND'
    : format === 'bo3'
      ? 'BEST OF 3 - PREMIER À 2 ROUNDS'
      : 'BEST OF 5 - PREMIER À 3 ROUNDS';

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="tactical-grid fixed inset-0 opacity-20" />
      
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <button
              data-testid="back-to-tournament"
              onClick={() => navigate(-1)}
              className="font-secondary text-xs text-[#ff4500] uppercase mb-3 sm:mb-4 hover:text-[#ff4500]/80"
            >
              ← RETOUR AU TOURNOI
            </button>
            <h1 className="font-primary font-black text-xl sm:text-2xl lg:text-3xl tracking-tighter uppercase">MATCH EN COURS</h1>
            <p className="font-secondary text-xs text-muted-foreground mt-1 sm:mt-2 tracking-wider">{formatText}</p>
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          {match.status === 'ready' && (
            <div className="mb-6 sm:mb-8 text-center">
              <button
                data-testid="start-match-btn"
                onClick={openBanModal}
                className="bg-[#ff4500] text-black font-bold uppercase tracking-widest px-6 sm:px-10 lg:px-12 py-3 sm:py-4 hover:bg-[#ff4500]/90 transition-colors clip-corner font-secondary text-xs sm:text-sm w-full sm:w-auto"
              >
                DÉMARRER LE MATCH
              </button>
            </div>
          )}

          <Dialog open={showBanModal} onOpenChange={setShowBanModal}>
            <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-primary font-black text-[#ff4500] uppercase">BAN DE MAPS</DialogTitle>
                {isQuartersSingleElim ? (
                  <>
                    <p className="font-secondary text-xs text-muted-foreground">Sélectionnez 2 maps (avec leur mode) à bannir.</p>
                    <p className="font-secondary text-xs text-[#ff4500] mt-2">Quarts de finale BO1 — La map et le mode seront tirés au sort</p>
                  </>
                ) : (
                  <p className="font-secondary text-xs text-muted-foreground">
                    Chaque équipe bannit une map par mode. Ordre: Hardpoint (Éq1→Éq2), S&D (Éq2→Éq1), Overload (Éq1→Éq2)
                  </p>
                )}
              </DialogHeader>

              {isQuartersSingleElim ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-4">
                    {maps.flatMap(mapData =>
                      (mapData.modes || []).map(mode => {
                        const banned = isBanned(mapData.name, mode);
                        return (
                          <button
                            key={`${mapData.name}_${mode}`}
                            type="button"
                            onClick={() => toggleBan(mapData.name, mode)}
                            className={`relative border-2 p-3 sm:p-4 text-left transition-all ${
                              banned ? 'border-red-500 bg-red-500/20' : 'border-[#262626] hover:border-[#ff4500]/50 bg-[#1a1a1a]'
                            }`}
                          >
                            {mapData.image && (
                              <div className="aspect-video mb-2 overflow-hidden">
                                <img src={mapData.image} alt={mapData.name} className="w-full h-full object-cover opacity-80" />
                              </div>
                            )}
                            <p className="font-primary font-bold text-sm text-white uppercase">{mapData.name}</p>
                            <p className="font-secondary text-xs text-[#ff4500] uppercase mt-0.5">{mode}</p>
                            {banned && <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 uppercase">BAN</span>}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <p className="font-secondary text-xs text-muted-foreground mb-2">{selectedBans.length}/2 bans</p>
                </>
              ) : (
                <>
                  {banStep < 6 ? (
                    <>
                      <div className="py-2">
                        <p className="font-secondary text-xs text-[#ff4500] uppercase mb-2">
                          Étape {banStep + 1}/6 — {BAN_STEPS_SEMI_FINAL[banStep].label} bannit ({BAN_STEPS_SEMI_FINAL[banStep].mode})
                        </p>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-4">
                        {getAvailableForMode(BAN_STEPS_SEMI_FINAL[banStep].mode).map(({ map: mapData }) => (
                          <button
                            key={`${mapData.name}_${mapData.modes}`}
                            type="button"
                            onClick={() => selectBanSemiFinal(mapData.name, BAN_STEPS_SEMI_FINAL[banStep].mode)}
                            className="border-2 border-[#262626] hover:border-[#ff4500] p-3 bg-[#1a1a1a] text-left transition-all"
                          >
                            {mapData.image && (
                              <div className="aspect-video mb-2 overflow-hidden">
                                <img src={mapData.image} alt={mapData.name} className="w-full h-full object-cover opacity-80" />
                              </div>
                            )}
                            <p className="font-primary font-bold text-xs text-white uppercase">{mapData.name}</p>
                          </button>
                        ))}
                      </div>
                      {selectedBans.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedBans.map((b, i) => (
                            <span key={i} className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5">
                              {b.map_name} ({b.mode})
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="font-secondary text-sm text-[#00ff9d] py-4">Tous les bans sont terminés. Cliquez pour démarrer.</p>
                  )}
                  <p className="font-secondary text-xs text-muted-foreground mb-2">{selectedBans.length}/6 bans</p>
                </>
              )}

              <DialogFooter>
                <button onClick={() => setShowBanModal(false)} className="font-secondary text-xs uppercase text-muted-foreground hover:text-white">
                  Annuler
                </button>
                <button
                  onClick={() => startMatch(selectedBans)}
                  disabled={!isQuartersSingleElim && selectedBans.length !== 6}
                  className="bg-[#ff4500] text-black font-bold uppercase px-6 py-2 font-secondary text-xs hover:bg-[#ff4500]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Démarrer le match
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {match.status === 'completed' && (
            <>
              {match.banned_maps && match.banned_maps.length > 0 && (
                <div className="mb-6 sm:mb-8">
                  <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 corner-decoration">
                    <h2 className="font-secondary text-xs text-muted-foreground uppercase tracking-wider mb-3">MAPS BANNIES</h2>
                    <div className="flex flex-wrap gap-2">
                      {match.banned_maps.map((ban, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 bg-red-500/20 border border-red-500/50 px-3 py-1.5 font-secondary text-xs uppercase"
                        >
                          <span className="text-red-400 font-bold">{ban.map_name}</span>
                          <span className="text-red-400/80">—</span>
                          <span className="text-red-300">{ban.mode}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="mb-6 sm:mb-8 text-center">
                <div className="bg-[#0a0a0a] border border-[#ff4500] p-4 sm:p-6 lg:p-8 inline-block">
                  <h2 className="font-primary font-black text-xl sm:text-2xl uppercase text-[#ff4500] mb-2">VAINQUEUR</h2>
                  <p className="font-secondary text-base sm:text-lg">{teams[match.winner_id]?.name}</p>
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
            <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 lg:p-8 corner-decoration relative" data-testid="team1-card">
              <h3 className="font-secondary text-xs text-muted-foreground uppercase tracking-wider mb-3 sm:mb-4">ÉQUIPE 1</h3>
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                {team1?.logo_url && (
                  <img 
                    src={team1.logo_url} 
                    alt={team1.name} 
                    className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <h2 className="font-primary font-black text-xl sm:text-2xl lg:text-3xl uppercase break-words">{team1?.name || 'TBD'}</h2>
              </div>
              {team1?.players?.length > 0 && (
                <ul className="flex flex-wrap gap-1.5 mb-3 sm:mb-4">
                  {team1.players.map((p, i) => (
                    <li key={i} className="font-secondary text-xs text-white/80 bg-white/5 px-2 py-1 rounded">{p}</li>
                  ))}
                </ul>
              )}
              <div className="font-secondary text-4xl sm:text-5xl font-black text-[#ff4500]">{match.team1_wins}</div>
              <p className="font-secondary text-xs text-muted-foreground mt-2">ROUNDS GAGNÉS</p>
            </div>

            <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 lg:p-8 corner-decoration relative" data-testid="team2-card">
              <h3 className="font-secondary text-xs text-muted-foreground uppercase tracking-wider mb-3 sm:mb-4">ÉQUIPE 2</h3>
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                {team2?.logo_url && (
                  <img 
                    src={team2.logo_url} 
                    alt={team2.name} 
                    className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <h2 className="font-primary font-black text-xl sm:text-2xl lg:text-3xl uppercase break-words">{team2?.name || 'TBD'}</h2>
              </div>
              {team2?.players?.length > 0 && (
                <ul className="flex flex-wrap gap-1.5 mb-3 sm:mb-4">
                  {team2.players.map((p, i) => (
                    <li key={i} className="font-secondary text-xs text-white/80 bg-white/5 px-2 py-1 rounded">{p}</li>
                  ))}
                </ul>
              )}
              <div className="font-secondary text-4xl sm:text-5xl font-black text-[#ff4500]">{match.team2_wins}</div>
              <p className="font-secondary text-xs text-muted-foreground mt-2">ROUNDS GAGNÉS</p>
            </div>
          </div>

          {(match.status === 'in_progress' || match.status === 'completed') && match.rounds && match.rounds.length > 0 && (
            <>
              {match.banned_maps && match.banned_maps.length > 0 && (
                <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 corner-decoration relative mb-6 sm:mb-8">
                  <h2 className="font-secondary text-xs text-muted-foreground uppercase tracking-wider mb-3">MAPS BANNIES</h2>
                  <div className="flex flex-wrap gap-2">
                    {match.banned_maps.map((ban, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 bg-red-500/20 border border-red-500/50 px-3 py-1.5 font-secondary text-xs uppercase"
                      >
                        <span className="text-red-400 font-bold">{ban.map_name}</span>
                        <span className="text-red-400/80">—</span>
                        <span className="text-red-300">{ban.mode}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-[#0a0a0a] border border-[#262626] p-4 sm:p-6 lg:p-8 corner-decoration relative mb-6 sm:mb-8">
                <h2 className="font-primary font-black text-lg sm:text-xl uppercase mb-4 sm:mb-6">
                  PLANNING DES {match.rounds.length} ROUND{match.rounds.length > 1 ? 'S' : ''} - STYLE CDL
                  {!isQuartersSingleElim && (
                    <span className="block font-secondary text-xs text-muted-foreground font-normal mt-1">Game 1: Hardpoint · Game 2: Search & Destroy · Game 3: Overload · ...</span>
                  )}
                </h2>
                <div className={`grid grid-cols-2 sm:grid-cols-3 ${format === 'bo5' ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-3 sm:gap-4`}>
                  {match.rounds.map((r, idx) => (
                    <div 
                      key={idx} 
                      className={`border relative overflow-hidden ${
                        r.completed ? 'border-[#00ff9d] bg-[#00ff9d]/5' : 
                        idx === currentRound ? 'border-[#ff4500] bg-[#ff4500]/5' : 
                        'border-[#262626]'
                      }`}
                      data-testid={`round-plan-${idx}`}
                    >
                      <div className="relative">
                        <img 
                          src={r.map_image} 
                          alt={r.map_name} 
                          className="w-full h-32 object-cover opacity-80"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                        <div className="absolute top-2 left-2 right-2 text-center">
                          <span className="font-secondary text-xs text-white/80 uppercase bg-black/60 px-2 py-1">Game {r.round_number}: {r.mode}</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="font-primary font-bold text-sm text-white uppercase text-center">{r.map_name}</p>
                          <p className="font-secondary text-xs text-[#00ff9d] uppercase text-center mt-1">{r.mode}</p>
                          {r.completed && (
                            <div className="text-center mt-2 space-y-1">
                              <span className="font-secondary text-xs bg-[#00ff9d]/20 text-[#00ff9d] px-2 py-1 border border-[#00ff9d]/50">✓ {r.team1_score} - {r.team2_score}</span>
                              {r.player_stats?.length > 0 && (
                                <div className="font-secondary text-[10px] text-white/70 mt-1">Stats joueurs</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {(match.status === 'in_progress' || match.status === 'completed') && (
            <div className="bg-[#0a0a0a] border border-[#262626] p-8 corner-decoration relative">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-primary font-black text-xl uppercase">
                  {editingRound !== null ? `ÉDITER GAME ${editingRound + 1}` : `GAME ${currentRound + 1}`}
                  {round?.mode && <span className="block font-secondary text-sm text-[#00ff9d] font-normal">({round.mode})</span>}
                </h2>
                {round && (
                  <span className="font-secondary text-xs text-[#00ff9d] uppercase">
                    {round.map_name ? `${round.map_name} - ${round.mode}` : 'EN ATTENTE'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">{team1?.name} - Score</label>
                  <input
                    data-testid="team1-score-input"
                    type="number"
                    value={team1Score}
                    onChange={(e) => setTeam1Score(parseInt(e.target.value) || 0)}
                    className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-2xl font-secondary focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">{team2?.name} - Score</label>
                  <input
                    data-testid="team2-score-input"
                    type="number"
                    value={team2Score}
                    onChange={(e) => setTeam2Score(parseInt(e.target.value) || 0)}
                    className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-2xl font-secondary focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {round?.mode && (
                <div className="mb-6 p-4 bg-[#1a1a1a] border border-[#262626] overflow-x-auto">
                  <h3 className="font-secondary text-xs text-muted-foreground uppercase mb-3">Photo du score</h3>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScoreImageFileChange}
                    className="w-full mb-4 bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm file:mr-4 file:border-0 file:bg-[#ff4500] file:px-3 file:py-1 file:font-bold file:text-black"
                  />
                  {scoreImageUrl ? (
                    <img
                      src={scoreImageUrl}
                      alt={`Aperçu score round ${round.round_number}`}
                      className="w-full max-h-[420px] object-contain border border-white/10 bg-black/30"
                    />
                  ) : (
                    <p className="font-secondary text-xs text-muted-foreground">Ajoute une photo locale du scoreboard pour ce round.</p>
                  )}
                </div>
              )}

              <div className="flex gap-4 mb-8">
                <button
                  data-testid="submit-round-btn"
                  onClick={() => submitRound(editingRound)}
                  className="flex-1 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                >
                  {editingRound !== null ? 'SAUVEGARDER' : 'VALIDER LE ROUND'}
                </button>
                {editingRound !== null && (
                  <button
                    onClick={() => {
                      setEditingRound(null);
                      setTeam1Score(0);
                      setTeam2Score(0);
                      setScoreImageUrl('');
                      setPlayerStats({});
                      const firstIncomplete = match.rounds.findIndex(r => !r.completed);
                      setCurrentRound(firstIncomplete !== -1 ? firstIncomplete : 0);
                    }}
                    className="px-6 py-4 border border-white/20 text-white hover:bg-white/5 transition-colors font-secondary text-sm uppercase"
                  >
                    ANNULER
                  </button>
                )}
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <h3 className="font-secondary text-xs text-muted-foreground uppercase mb-4">ROUNDS</h3>
                <div className="space-y-2">
                  {match.rounds.map((r, idx) => (
                    <div key={idx} className="py-3 px-4 bg-[#1a1a1a] border border-[#262626] hover:border-[#ff4500]/30 transition-colors" data-testid={`round-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="font-secondary text-sm font-bold">Round {r.round_number}</span>
                          <span className="font-secondary text-xs text-[#00ff9d]">{r.map_name} - {r.mode}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-secondary text-sm font-bold">
                            {r.completed ? `${r.team1_score} - ${r.team2_score}` : 'En attente'}
                          </span>
                          {r.completed && (
                            <button
                              onClick={() => editRound(idx)}
                              className="font-secondary text-xs text-[#ff4500] hover:text-[#ff4500]/80 uppercase"
                              data-testid={`edit-round-${idx}`}
                            >
                              ÉDITER
                            </button>
                          )}
                        </div>
                      </div>
                      {r.completed && r.score_image_url ? (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="font-secondary text-[10px] text-muted-foreground uppercase mb-2">Photo du score</p>
                          <img
                            src={r.score_image_url}
                            alt={`Score round ${r.round_number}`}
                            className="w-full max-h-[520px] object-contain border border-white/10 bg-black/30"
                          />
                        </div>
                      ) : r.completed && (
                        <p className="mt-3 pt-3 border-t border-white/10 font-secondary text-xs text-muted-foreground">
                          Aucune photo du score enregistrée.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/public" element={<PublicDashboard />} />
          <Route path="/public/tournament/:id" element={<PublicTournamentView />} />
          <Route path="/public/match/:id" element={<PublicMatchView />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/tournament/:id" element={<ProtectedRoute><TournamentDetails /></ProtectedRoute>} />
          <Route path="/match/:id" element={<ProtectedRoute><MatchView /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/public" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
